/**
 * FindReplacementsDialog — admin modal triggered from AdminShiftDetail
 * "Find Replacements" button. Lets admin run a Smart Replacement search
 * with optional manual reason, then shows past searches + per-search
 * candidate roster (Pending/Accepted/Declined/Expired).
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Loader2, RefreshCcw, Zap, Users, ChevronRight, ChevronDown, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { AIAutoFilledBadge } from './AIBadges';

const REASON_OPTIONS = [
  { value: 'manual',                   label: 'Manual search' },
  { value: 'no_show',                  label: 'No-show — apply urgent rate if enabled' },
  { value: 'last_minute_cancellation', label: 'Last-minute cancellation — apply urgent rate' },
  { value: 'last_minute_removal',      label: 'Last-minute removal — apply urgent rate' },
  { value: 'missed_day_confirm',       label: 'Missed day-confirm — apply urgent rate' },
];

const STATUS_BADGE = {
  pending:  'bg-amber-100 text-amber-800 border-amber-300',
  accepted: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  declined: 'bg-red-100 text-red-800 border-red-300',
  expired:  'bg-gray-100 text-gray-700 border-gray-300',
};

export default function FindReplacementsDialog({ open, onOpenChange, shiftId, onAfterTrigger }) {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [reason, setReason] = useState('manual');
  const [expandedSearch, setExpandedSearch] = useState(null);
  const [candidates, setCandidates] = useState({});

  const load = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const r = await api.get(`/smart-replacement/searches/${shiftId}`);
      setSearches(r.data || []);
    } catch (e) {
      toast.error('Failed to load search history');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const trigger = async () => {
    setTriggering(true);
    try {
      const r = await api.post(`/smart-replacement/trigger/${shiftId}`, { reason });
      if (r.data?.duplicate) {
        toast.info('A replacement search ran in the last 5 minutes — opening the existing one.');
      } else {
        toast.success(`Found ${r.data.candidates_found} candidates · notified ${r.data.candidates_notified}`);
      }
      await load();
      onAfterTrigger?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to trigger search');
    } finally {
      setTriggering(false);
    }
  };

  const expand = async (searchId) => {
    if (expandedSearch === searchId) {
      setExpandedSearch(null);
      return;
    }
    setExpandedSearch(searchId);
    if (!candidates[searchId]) {
      try {
        const r = await api.get(`/smart-replacement/searches/${searchId}/candidates`);
        setCandidates((prev) => ({ ...prev, [searchId]: r.data || [] }));
      } catch (e) {
        toast.error('Failed to load candidates');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="find-replacements-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-cyan-600" /> Find Replacements
            <span className="ml-2 inline-flex items-center gap-0.5 font-semibold rounded-full border bg-cyan-50 text-cyan-700 border-cyan-200 px-1.5 py-0.5 text-[10px]" data-testid="find-rep-ai-pill">
              Powered by AI
            </span>
          </DialogTitle>
          <DialogDescription>
            iEvntür AI ranks qualified talent and notifies them instantly — first acceptance auto-books the slot.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded border p-3 bg-cyan-50/40 space-y-2">
          <Label className="text-sm font-medium">Reason</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="find-rep-reason-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REASON_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={trigger}
            disabled={triggering}
            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="find-rep-trigger-btn"
          >
            {triggering
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Searching…</>
              : <><Zap className="w-4 h-4 mr-1" /> Run AI Smart Replacement</>}
          </Button>
        </div>

        <div className="flex items-center justify-between pt-2">
          <h4 className="text-sm font-semibold flex items-center gap-1.5">
            <Receipt className="w-4 h-4" /> Search history
          </h4>
          <Button size="sm" variant="ghost" onClick={load}>
            <RefreshCcw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {loading ? (
          <div className="text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> loading…</div>
        ) : searches.length === 0 ? (
          <div className="text-xs text-gray-500">No searches yet.</div>
        ) : (
          <ul className="space-y-2" data-testid="searches-list">
            {searches.map((s) => (
              <li key={s.id} className="border rounded text-sm" data-testid={`search-row-${s.id}`}>
                <button
                  type="button"
                  onClick={() => expand(s.id)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium capitalize">{s.trigger_reason?.replaceAll('_', ' ')}</span>
                    {s.is_urgent && (
                      <Badge className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px]">
                        <Zap className="w-3 h-3 mr-0.5 inline" /> URGENT ${s.offer_rate?.toFixed(2)}/hr
                      </Badge>
                    )}
                    <span className="text-xs text-gray-500">
                      {s.candidates_found} found · {s.candidates_notified} notified
                    </span>
                    {s.replacement_filled && (
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px]">FILLED</Badge>
                    )}
                  </div>
                  <span className="text-[11px] text-gray-500 inline-flex items-center gap-1">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : ''}
                    {expandedSearch === s.id ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </span>
                </button>
                {expandedSearch === s.id && (
                  <div className="px-3 pb-2 border-t bg-gray-50/60">
                    <div className="text-[11px] text-gray-600 my-1.5 flex gap-3 flex-wrap">
                      <span>Pending: <b>{s.pending_count}</b></span>
                      <span>Accepted: <b>{s.accepted_count}</b></span>
                      <span>Declined: <b>{s.declined_count}</b></span>
                      <span>Expired: <b>{s.expired_count}</b></span>
                    </div>
                    {(candidates[s.id] || []).length === 0 ? (
                      <div className="text-xs text-gray-500 py-1">No candidates recorded.</div>
                    ) : (
                      <ul className="space-y-1 pb-2">
                        {(candidates[s.id] || []).map((c) => {
                          const isAccepted = c.response_status === 'accepted';
                          return (
                          <li key={c.id} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2 min-w-0">
                              <Users className="w-3 h-3 text-gray-400 shrink-0" />
                              <span className="truncate">{c.name || c.email}</span>
                              <span className="text-gray-500">AI score {c.match_score?.toFixed(1)}</span>
                              {isAccepted && (
                                <AIAutoFilledBadge testId={`auto-filled-${c.id}`} />
                              )}
                            </div>
                            <Badge className={`text-[10px] ${STATUS_BADGE[c.response_status] || ''}`}>
                              {c.response_status}
                            </Badge>
                          </li>
                        );})}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
